/**
 * Execution map — the "Today" layer of the execution model.
 *
 * A doc note is a *period*; the period's list is a set of pointers to nodes,
 * each tickable as worked-on (in that note) and done (the node's Done tag). See
 * docs/internal/2026-09-06-execution-model-design.md §2, §3 and §11.1.
 *
 * Storage: `doc.getMap("execution")`, one plain-object entry per
 * (period, node), keyed `"<noteId>/<nodeId>"` (IDs with separators or reserved
 * prefixes use `entry:` + JSON [noteId, nodeId]):
 *
 *   { noteId, nodeId, addedAt, order, pinned, workedOn: ["YYYY-MM-DD", ...],
 *     removedAt? }
 *
 * Entries are never deleted. The original entry is retained as a baseline;
 * edits use independent keys in the same map, prefixed `field:` followed by
 * JSON [noteId, nodeId, field, date?]. Each worked date has its own boolean.
 * Concurrent pin/order/removal/day edits therefore merge independently, even
 * if two clients first create the same item while offline. Legacy baselines
 * remain readable without a destructive conversion or nested-map replacement.
 *
 * All functions take the YjsDocManager (needs `.doc`, `.docNotes`,
 * `.metadata`) and are otherwise pure; the bridge wraps them with the
 * update-depth bookkeeping and storage-health checks.
 */

import { USER_ORIGIN, MIGRATION_ORIGIN } from "./origins.js";

export const EXECUTION_MAP_NAME = "execution";
export const EXECUTION_FORMAT_KEY = "executionFormat";
export const EXECUTION_FORMAT_V1 = "v1";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const FIELD_PREFIX = "field:";
const ENTRY_PREFIX = "entry:";

function fieldKey(noteId, nodeId, field, date) {
  return FIELD_PREFIX + JSON.stringify(date === undefined
    ? [noteId, nodeId, field] : [noteId, nodeId, field, date]);
}

/** Resolve field edits over the preserved v1 entries in two linear passes. */
function resolvedEntries(map, noteId = null, nodeId = null) {
  const entries = new Map();
  map.forEach((raw, key) => {
    // A note/task lookup should not normalize every historical item's dates.
    // Filter by the same string identities normalizeEntry uses first; field
    // overrides are still resolved below, including legacy baseline keys.
    if ((noteId && String(raw?.noteId) !== noteId)
      || (nodeId && String(raw?.nodeId) !== nodeId)) return;
    const entry = normalizeEntry(raw);
    if (!entry || (noteId && entry.noteId !== noteId) || (nodeId && entry.nodeId !== nodeId)) return;
    entries.set(executionKey(entry.noteId, entry.nodeId), { ...entry, workedOn: new Set(entry.workedOn) });
  });
  map.forEach((value, key) => {
    if (!key.startsWith(FIELD_PREFIX)) return;
    let parts;
    try { parts = JSON.parse(key.slice(FIELD_PREFIX.length)); } catch { return; }
    if (!Array.isArray(parts)) return;
    const [note, node, field, date] = parts;
    const entry = entries.get(executionKey(note, node));
    if (!entry) return;
    if (field === "workedOn" && DATE_RE.test(date) && typeof value === "boolean") {
      if (value) entry.workedOn.add(date); else entry.workedOn.delete(date);
    } else if (field === "pinned" && typeof value === "boolean") entry.pinned = value;
    else if (field === "order" && Number.isFinite(value)) entry.order = value;
    else if (field === "removedAt") {
      if (typeof value === "string") entry.removedAt = value;
      else if (value === null) delete entry.removedAt;
    }
  });
  return [...entries.values()].map((entry) => ({ ...entry, workedOn: [...entry.workedOn].sort() }));
}

function writeField(docManager, noteId, nodeId, field, value, date) {
  docManager.doc.transact(() => {
    getExecutionMap(docManager).set(fieldKey(noteId, nodeId, field, date), value);
  }, USER_ORIGIN);
}

export function executionKey(noteId, nodeId) {
  // Imported IDs need not be UUIDs. Escape separators and reserved prefixes
  // while retaining the original keys for normal IDs and existing documents.
  if (String(noteId).includes('/') || String(nodeId).includes('/')
      || String(noteId).startsWith(FIELD_PREFIX) || String(noteId).startsWith(ENTRY_PREFIX)) {
    return ENTRY_PREFIX + JSON.stringify([String(noteId), String(nodeId)]);
  }
  return `${noteId}/${nodeId}`;
}

export function getExecutionMap(docManager) {
  return docManager.doc.getMap(EXECUTION_MAP_NAME);
}

/** Today's date as YYYY-MM-DD in local time (ticks are calendar days). */
export function localDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeEntry(raw) {
  if (!raw || typeof raw !== "object" || !raw.noteId || !raw.nodeId) return null;
  return {
    noteId: String(raw.noteId),
    nodeId: String(raw.nodeId),
    addedAt: typeof raw.addedAt === "string" ? raw.addedAt : new Date(0).toISOString(),
    order: Number.isFinite(raw.order) ? raw.order : 0,
    pinned: !!raw.pinned,
    workedOn: Array.isArray(raw.workedOn)
      ? [...new Set(raw.workedOn.filter((d) => typeof d === "string" && DATE_RE.test(d)))].sort()
      : [],
    ...(typeof raw.removedAt === "string" ? { removedAt: raw.removedAt } : {}),
    ...(typeof raw.linkedDocId === "string" && raw.linkedDocId ? { linkedDocId: raw.linkedDocId } : {}),
  };
}

/**
 * All entries, optionally for one period. Removed entries are excluded unless
 * `includeRemoved` is set (stats want them; lists do not).
 * @returns {Array<object>} entries sorted by (noteId, order, addedAt)
 */
export function listExecutionItems(docManager, { noteId = null, includeRemoved = false } = {}) {
  const map = getExecutionMap(docManager);
  const out = resolvedEntries(map, noteId).filter((e) => includeRemoved || !e.removedAt);
  out.sort((a, b) =>
    a.noteId.localeCompare(b.noteId) || a.order - b.order || a.addedAt.localeCompare(b.addedAt));
  return out;
}

export function getExecutionItem(docManager, noteId, nodeId) {
  return resolvedEntries(getExecutionMap(docManager), noteId, nodeId)[0] || null;
}

/**
 * Put a node on a period's list. Re-sending a removed item restores it (its
 * history is kept). Returns the entry.
 */
export function sendToExecution(docManager, noteId, nodeId, { order = null, pinned = null } = {}) {
  const existing = getExecutionItem(docManager, noteId, nodeId);
  if (order === null && (!existing || existing.removedAt)) {
    const siblings = listExecutionItems(docManager, { noteId });
    order = siblings.length ? Math.min(...siblings.map(s => s.order)) - 1 : 0;
  }
  let entry;
  if (existing) {
    const { removedAt: _gone, ...kept } = existing;
    entry = { ...kept };
    if (order !== null) entry.order = order;
    if (pinned !== null) entry.pinned = !!pinned;
  } else {
    entry = {
      noteId, nodeId,
      addedAt: new Date().toISOString(),
      order,
      pinned: !!pinned,
      workedOn: [],
    };
  }
  docManager.doc.transact(() => {
    const map = getExecutionMap(docManager);
    if (!existing) map.set(executionKey(noteId, nodeId), entry);
    if (existing?.removedAt) map.set(fieldKey(noteId, nodeId, "removedAt"), null);
    if (existing && order !== null) map.set(fieldKey(noteId, nodeId, "order"), order);
    if (pinned !== null) map.set(fieldKey(noteId, nodeId, "pinned"), !!pinned);
  }, USER_ORIGIN);
  return getExecutionItem(docManager, noteId, nodeId);
}

/** Take a node off a period's list; the entry stays with `removedAt` set. */
export function removeFromExecution(docManager, noteId, nodeId) {
  const existing = getExecutionItem(docManager, noteId, nodeId);
  if (!existing || existing.removedAt) return false;
  writeField(docManager, noteId, nodeId, "removedAt", new Date().toISOString());
  return true;
}

/** Tick (or untick) a day as worked on. `date` defaults to today. */
export function tickWorked(docManager, noteId, nodeId, date = localDateString(), on = true) {
  if (!DATE_RE.test(date)) throw new Error(`tickWorked: bad date ${date}`);
  const existing = getExecutionItem(docManager, noteId, nodeId);
  if (!existing) return null;
  writeField(docManager, noteId, nodeId, "workedOn", !!on, date);
  return getExecutionItem(docManager, noteId, nodeId);
}

/**
 * The list checkbox describes work in this note, not whether today was ticked.
 * Checking records the click's date; opening a list never changes its dates.
 * Explicit unchecking clears only the dates that were displayed to the user,
 * preserving dates added elsewhere since that rendering and other notes' work.
 */
export function setExecutionWorked(docManager, noteId, nodeId, on, observedDates = [], date = localDateString()) {
  const existing = getExecutionItem(docManager, noteId, nodeId);
  if (!existing || existing.removedAt) return existing;
  if (on) return existing.workedOn.length ? existing : tickWorked(docManager, noteId, nodeId, date, true);
  if (!Array.isArray(observedDates)) throw new Error('Invalid worked dates');
  const dates = [...new Set(observedDates)].filter(day => existing.workedOn.includes(day));
  docManager.doc.transact(() => {
    const map = getExecutionMap(docManager);
    for (const day of dates) map.set(fieldKey(noteId, nodeId, 'workedOn', day), false);
  }, USER_ORIGIN);
  return getExecutionItem(docManager, noteId, nodeId);
}

export function setPinned(docManager, noteId, nodeId, pinned) {
  const existing = getExecutionItem(docManager, noteId, nodeId);
  if (!existing) return null;
  writeField(docManager, noteId, nodeId, "pinned", !!pinned);
  return getExecutionItem(docManager, noteId, nodeId);
}

/** Reorder a period's list; ids not mentioned keep their relative order after the given ones. */
export function setExecutionOrder(docManager, noteId, orderedNodeIds) {
  const items = listExecutionItems(docManager, { noteId });
  const byNode = new Map(items.map((e) => [e.nodeId, e]));
  const seq = [...orderedNodeIds.filter((id) => byNode.has(id)),
               ...items.map((e) => e.nodeId).filter((id) => !orderedNodeIds.includes(id))];
  docManager.doc.transact(() => {
    const map = getExecutionMap(docManager);
    seq.forEach((nodeId, i) => {
      const e = byNode.get(nodeId);
      if (e.order !== i) map.set(fieldKey(noteId, nodeId, "order"), i);
    });
  }, USER_ORIGIN);
}

/**
 * Carry-over helper: the previous period's items that were not done and not
 * removed, with how many consecutive periods each has been carried without a
 * worked tick. Pure read; the caller decides what to send.
 * @param {Array<object>} notesSorted - doc notes sorted by `created` ascending
 * @param {(nodeId: string) => boolean} isDone
 */
export function carryCandidates(docManager, notesSorted, currentNoteId, isDone) {
  const idx = notesSorted.findIndex((n) => n.id === currentNoteId);
  if (idx <= 0) return [];
  const prev = notesSorted[idx - 1];
  const all = listExecutionItems(docManager, { includeRemoved: true });
  const byNote = new Map();
  for (const e of all) {
    if (!byNote.has(e.noteId)) byNote.set(e.noteId, new Map());
    byNote.get(e.noteId).set(e.nodeId, e);
  }
  // `all` already contains the resolved, sorted entries. Reuse that snapshot
  // instead of scanning and normalizing the entire history two more times.
  const alreadyHere = new Set([...(byNote.get(currentNoteId)?.values() || [])]
    .filter((e) => !e.removedAt).map((e) => e.nodeId));
  const candidates = [];
  for (const e of byNote.get(prev.id)?.values() || []) {
    if (e.removedAt || isDone(e.nodeId) || alreadyHere.has(e.nodeId)) continue;
    // Count back through consecutive earlier periods where this node was listed and never ticked.
    let carried = 0;
    for (let j = idx - 1; j >= 0; j--) {
      const item = byNote.get(notesSorted[j].id)?.get(e.nodeId);
      if (!item || item.removedAt || item.workedOn.length) break;
      carried++;
    }
    candidates.push({ ...e, carriedWithoutWork: carried });
  }
  return candidates;
}

/**
 * Bulk-write entries (import of a per-map JSON export). Union semantics:
 * Existing choices are kept and incoming worked dates are unioned. Nothing
 * is removed. The export/import shape remains the resolved plain entry.
 */
export function setExecutionItems(docManager, entries, origin = USER_ORIGIN) {
  if (!Array.isArray(entries)) return 0;
  const map = getExecutionMap(docManager);
  const existingEntries = new Map(resolvedEntries(map).map((e) => [executionKey(e.noteId, e.nodeId), e]));
  let written = 0;
  docManager.doc.transact(() => {
    for (const raw of entries) {
      const e = normalizeEntry(raw);
      if (!e) continue;
      const key = executionKey(e.noteId, e.nodeId);
      const existing = existingEntries.get(key);
      if (!existing) {
        map.set(key, e);
        existingEntries.set(key, { ...e, workedOn: [...e.workedOn] });
        written++;
      }
      // Even first imports write each date independently: two offline imports
      // may create the same baseline, and either baseline can win during sync.
      for (const date of e.workedOn) {
        if (!existing || !existing.workedOn.includes(date)) {
          map.set(fieldKey(e.noteId, e.nodeId, "workedOn", date), true);
          if (existing) existing.workedOn.push(date);
          written++;
        }
      }
    }
    docManager.metadata.set(EXECUTION_FORMAT_KEY, EXECUTION_FORMAT_V1);
  }, origin);
  return written;
}

/**
 * Reconcile legacy doc-note `refnotes` ({nodeId, nodeTitle, linkedDocId?})
 * into execution entries. Runs on every open, not once: it unions any refnote
 * that has no execution entry (existing entries are never overwritten), so a
 * refnote written by an older client during the rollout window is never lost.
 * `refnotes` are left in place (readers of the old field keep working); the
 * metadata marker only says "readers should prefer the execution map".
 * Returns { migrated: whether the marker was newly set, count: entries added }.
 */
export function migrateRefnotesToExecution(docManager) {
  const map = getExecutionMap(docManager);
  const notes = docManager.docNotes.toArray();
  const existingKeys = new Set(resolvedEntries(map).map((e) => executionKey(e.noteId, e.nodeId)));
  const firstTime = docManager.metadata.get(EXECUTION_FORMAT_KEY) !== EXECUTION_FORMAT_V1;
  const missing = [];
  for (const note of notes) {
    if (!note || !note.id || !Array.isArray(note.refnotes)) continue;
    note.refnotes.forEach((ref, i) => {
      const nodeId = ref && (ref.nodeId || (typeof ref === "string" ? ref : null));
      if (!nodeId || existingKeys.has(executionKey(note.id, nodeId))) return;
      existingKeys.add(executionKey(note.id, nodeId));
      const entry = {
        noteId: note.id, nodeId,
        addedAt: typeof note.created === "string" ? note.created : new Date(0).toISOString(),
        order: i, pinned: false, workedOn: [],
      };
      if (ref && typeof ref === "object" && ref.linkedDocId) entry.linkedDocId = ref.linkedDocId;
      missing.push(entry);
    });
  }
  if (!missing.length && !firstTime) return { migrated: false, count: 0 };
  docManager.doc.transact(() => {
    for (const e of missing) map.set(executionKey(e.noteId, e.nodeId), e);
    if (firstTime) docManager.metadata.set(EXECUTION_FORMAT_KEY, EXECUTION_FORMAT_V1);
  }, MIGRATION_ORIGIN);
  return { migrated: firstTime, count: missing.length };
}
